import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  AnyoPromptTemplates,
  ChatLLMAdminRequest,
  PackService,
} from '../../../services/packs.service';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { TherapistGridRequest } from '../../../models/therapistGridRequest';
import { TherapistService } from '../../../services/therapist.service';
import { MasterdataService } from '../../../services/masterdata.service';

export interface ChatMessage {
  text: string;
  question: string;
  options: string[];
  end_message: string;
  aux: {
    m_id: string;
    data: string;
  };
  slides: {
    title: string;
    description: string;
  }[];
  id: string;
  not_applicable?: boolean;
  secondary_expert?: {
    m_id?: string;
  };
}

@Component({
  selector: 'app-template-check',
  templateUrl: './template-check.component.html',
  styleUrl: './template-check.component.scss',
})
export class TemplateCheckComponent {
  constructor(
    private packs: PackService,
    public utils: UtilsService,
    private auth: AuthService,
    private toastrService: ToastService,
    private therapistService: TherapistService,
    private masterdataService: MasterdataService,
  ) {}

  templateOptions: { label: string; value: string }[] = Object.entries(
    AnyoPromptTemplates,
  ).map(([label, value]) => {
    return {
      label: this.utils.convertToTitleCase(label),
      value: value,
    };
  });
  selectedTemplate!: AnyoPromptTemplates;
  promptText: string = '';
  loading = false;
  promptLoading = false;
  messages: ChatMessage[] = [];
  messageText = '';
  uniqueId = '';
  expertList: string = '';
  concernList: string = '';

  placeholders: { key: string; value: string }[] = [];

  @ViewChild('lastMessage') lastMessage!: ElementRef;

  scrollToLastMessage(messageId: string): void {
    setTimeout(() => {
      const lastMessageElement = document.getElementById(messageId);
      if (lastMessageElement) {
        lastMessageElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
  }

  extractPlaceholders(input: string): void {
    if (!input) {
      this.placeholders = [];
      return;
    }

    const matches = input.match(/\$(\w+)\$/g) || [];

    this.placeholders = Array.from(new Set(matches)).map((match) => ({
      key: match.replace(/\$/g, ''),
      value: '',
    }));
  }

  onTextareaChange(value: string) {
    this.promptText = value;
    this.extractPlaceholders(value);
  }

  onInputChange(key: string, value: string): void {
    this.placeholders = this.placeholders.map((item) =>
      item.key === key ? { ...item, value } : item,
    );
  }

  extractUniquePlaceholders(text: string): string[] {
    const matches = text.match(/\$(.*?)\$/g);
    return matches
      ? Array.from(new Set(matches.map((match) => match.slice(1, -1))))
      : [];
  }

  getTemplate($event: AnyoPromptTemplates) {
    this.loading = true;
    this.packs.getAnyoPromptTemplate({ templateId: $event }).subscribe({
      next: (value) => {
        const rawTemplate =
          typeof value.template === 'string' ? value.template.trim() : '';

        this.promptText = rawTemplate.replace(/[;]/g, '').toString();

        (this.placeholders = this.extractUniquePlaceholders(
          this.promptText,
        ).map((data) => {
          if (data === 'CONCERNS') {
            return { key: data, value: this.concernList };
          }
          if (data === 'ALL_EXPERT_LIST') {
            return { key: data, value: this.expertList };
          }
          return { key: data, value: '' };
        })),
          (this.loading = false);
      },
      error: (err) => {
        const e = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(e.description);
        this.loading = false;
      },
    });
  }

  sendMessage(text: string) {
    this.messageText = text;
    this.testPrompt();
  }

  testPrompt() {
    this.promptLoading = true;
    if (
      this.placeholders.length &&
      this.placeholders.some((data) => data.value === '')
    ) {
      this.toastrService.showAnyoErrorToast(
        'Placeholder value should not empty!!',
      );
      this.promptLoading = false;
      return;
    }
    if (this.promptText === '') {
      this.toastrService.showAnyoErrorToast(
        'Please Select Template before sending message!!',
      );
      this.promptLoading = false;
      return;
    }
    const req: ChatLLMAdminRequest = {
      message: this.messageText,
      promptTemplateId: this.selectedTemplate,
      prompt: this.promptText,
      placeHolders: this.placeholders,
      chatSessionId: this.uniqueId,
    };
    this.packs.chatWithPrompt(req).subscribe({
      next: (value) => {
        const newMessage = { ...value, id: 'msg-' + uuidv4() };
        this.messages.push(newMessage);
        this.messageText = '';
        setTimeout(() => this.scrollToLastMessage(newMessage.id), 200);
        this.promptLoading = false;
      },
      error: (err) => {
        const e = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(e.description);
        this.promptLoading = false;
      },
    });
  }

  clearChat() {
    this.messages = [];
    this.messageText = ' ';
    const uniqueId = uuidv4();
    this.uniqueId = uniqueId;
  }

  async fetchTherspist() {
    const req: TherapistGridRequest = {
      page: 1,
      pageSize: 100,
      export: false,
    };
    this.therapistService.listTherapist(req).subscribe({
      next: (value) => {
        this.expertList = JSON.stringify(value.data);
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Error while fetching therapist');
      },
    });
  }

  async loadMasterData(): Promise<void> {
    this.masterdataService.masterDataList().subscribe({
      next: (masterData) => {
        this.concernList = JSON.stringify(masterData.therapyConcerns);
      },
    });
  }

  ngOnInit() {
    this.loading = true;
    this.promptLoading = true;
    this.loadMasterData();
    this.auth.currentAuthStatus.subscribe((user) => {
      if (user) {
        this.fetchTherspist();
        const uniqueId = uuidv4();
        this.uniqueId = uniqueId;
        this.loading = false;
        this.promptLoading = false;
      }
    });
  }
}
